import Checkbox from 'components/shared/Checkbox/Checkbox';
import Icon from 'components/shared/Icon/Icon';
import RadioButton from 'components/shared/RadioButton/RadioButton';

import { ATTENDANCE_OPTIONS } from '../lib/config';

/**
 * TrainingAttestationCard - Admin-only card for training attendance and confirmation
 *
 * Displays:
 * - Training attendance radio buttons (on-time, late/left early, did not attend)
 * - Training confirmation checkboxes (enabled only when attended)
 *
 * @param {Object} props
 * @param {string} props.attendanceStatus - Current attendance selection
 * @param {Function} props.onAttendanceChange - Callback when attendance changes
 * @param {string[]} props.confirmations - Attestation confirmation texts from config
 * @param {boolean[]} props.confirmedItems - Array of booleans for each confirmation checkbox
 * @param {Function} props.onConfirmationChange - Callback(index, checked) when confirmation changes
 * @param {boolean} props.isCompleted - Whether the substep is already completed
 * @param {boolean} props.isUpdating - Whether an attendance update API call is in progress
 */
const TrainingAttestationCard = ({
  attendanceStatus,
  onAttendanceChange,
  confirmations = [],
  confirmedItems = [],
  onConfirmationChange,
  isCompleted,
  isUpdating = false
}) => {
  const isAttended = attendanceStatus === 'on_time' || attendanceStatus === 'late_or_left_early';
  const confirmationsEnabled = isAttended && !isCompleted;
  const attendanceDisabled = isCompleted || isUpdating;

  return (
    <div className="overflow-hidden rounded-xl border border-solid border-neutral-200 bg-white">
      <div className="flex items-center gap-2 border-0 border-b border-solid border-neutral-100 px-5 pb-4 pt-5">
        <div className="flex items-center justify-center rounded-lg bg-neutral-50 p-1.5">
          <Icon icon="aeob-mono" size={20} />
        </div>
        <h3 className="text-base font-500 text-neutral-900">Live working session attestation</h3>
      </div>

      <div className="flex flex-col gap-5 px-5 py-5">
        <div className="flex flex-col gap-2.5">
          <span className="text-sm font-400 text-neutral-600">Attendance:</span>
          <div className="flex flex-col gap-2">
            {ATTENDANCE_OPTIONS.map((option) => (
              <div key={option.value} className="cursor-pointer">
                <RadioButton
                  id={`attendance-${option.value}`}
                  isSelected={attendanceStatus === option.value}
                  name="attendanceStatus"
                  value={option.value}
                  label={option.label}
                  onChange={() => !attendanceDisabled && onAttendanceChange(option.value)}
                  wrapperClassName="radio--small"
                  disabled={attendanceDisabled}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="h-px w-full bg-neutral-200" />

        <div className="flex flex-col gap-2.5">
          <span className="text-sm font-400 text-neutral-600">Confirmation:</span>
          <div className="flex flex-col gap-2">
            {confirmations.map((text, index) => (
              <div key={index} className="flex items-start gap-3">
                <Checkbox
                  id={`attestation-confirmation-${index}`}
                  isChecked={confirmedItems[index] || false}
                  onChange={() => confirmationsEnabled && onConfirmationChange(index)}
                  disabled={!confirmationsEnabled}
                  label=""
                  className="pt-1"
                  size={16}
                />
                <label
                  htmlFor={`attestation-confirmation-${index}`}
                  className={`text-sm font-400 ${!confirmationsEnabled ? 'text-neutral-400' : 'text-neutral-800'} whitespace-normal`}>
                  {text}
                </label>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrainingAttestationCard;
